/* eslint-disable no-magic-numbers */

import {
  DateFormats,
  type DescriptionListItem,
  formatISODate,
  formatNumber,
  Icon,
  Inline,
  Tooltip,
  Typography,
} from '@omnipresentgroup/design-system';
import { useGetContractorInvoice } from '@omnipresentgroup/domains';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import {
  type BaseQuote,
  FeeType,
  type Fx,
  type InvoiceQuote,
} from '@omnipresentgroup/types';
import { useEffect, useState } from 'react';

const INVOICE_TOTAL_LABEL = 'Invoice total';
const EXCHANGE_RATE_LABEL = 'Exchange rate';
const TOTAL_LABEL = 'Amount';
const FEE_LABELS = new Map<FeeType, string>([
  [FeeType.MANAGEMENT, 'Management fee'],
  [FeeType.LIABILITY_SHIELD, 'Liability shield fee'],
]);

enum InvoiceItems {
  CONTRACTOR = 0,
  INVOICE_TOTAL = 1,
  EXCHANGE_RATE = 2,
  FEE = 3,
  TOTAL = 4,
}

const getPercentage = (amount: string): string => `${Number(amount) * 100}%`;

const getInvoiceTotalAmount = (quote: BaseQuote | InvoiceQuote) => {
  const baseTotalAmount = formatNumber({
    input: Number(quote.baseAmount.amount),
    currency: quote.baseAmount.currency,
  });
  const convertedTotalAmount = formatNumber({
    input: Number(quote.convertedAmount.amount),
    currency: quote.convertedAmount.currency,
  });

  return quote.fx?.conversion
    ? `${baseTotalAmount} ≈ ${convertedTotalAmount}`
    : baseTotalAmount;
};

const getExchangeRate = (fx: Fx) => `1 ${fx.conversion.fromCurrency} =
                      ${formatNumber({
                        input: Number(fx.conversion.effectiveRate),
                        maximumFractionDigits: 4,
                      })} ${fx.conversion.toCurrency}`;

type QuoteDescriptionItems = {
  item: InvoiceItems;
  label: string;
  tooltip?: string;
  value: JSX.Element | string;
};

export const useQuoteDescriptionList = (
  quote: BaseQuote | InvoiceQuote | undefined,
  showInvoiceQuoteDetails: boolean,
) => {
  const [quoteDescriptionItems, setQuoteDescriptionItems] = useState<
    QuoteDescriptionItems[]
  >([]);

  const { data: invoice, isFetching: isInvoiceLoading } =
    useGetContractorInvoice({
      id: (quote as InvoiceQuote)?.invoiceId,
      enabled: Boolean((quote as InvoiceQuote)?.invoiceId),
    });

  useEffect(() => {
    if (!quote) {
      return;
    }
    const listItems: QuoteDescriptionItems[] = [
      ...(showInvoiceQuoteDetails && (quote as InvoiceQuote)?.contractor
        ? [
            {
              item: InvoiceItems.CONTRACTOR,
              label: (quote as InvoiceQuote)?.contractor.name,
              value: '',
            },
          ]
        : []),
      {
        item: InvoiceItems.INVOICE_TOTAL,
        label: INVOICE_TOTAL_LABEL,
        value: getInvoiceTotalAmount(quote),
      },
      ...(quote.fx
        ? [
            {
              item: InvoiceItems.EXCHANGE_RATE,
              label: EXCHANGE_RATE_LABEL,
              value: getExchangeRate(quote.fx),
              tooltip: `We use multiple providers to ensure we use the best rates for currency conversions. We also include a ${getPercentage(
                quote.fx.markupRate,
              )} markup on this rate.`,
            },
          ]
        : []),
      ...quote.fees.map((fee) => ({
        item: InvoiceItems.FEE,
        label: `${FEE_LABELS.get(fee.type) || ''}${
          fee.month
            ? ` (${formatISODate(fee.month, DateFormats.DateWithoutDay)})`
            : ''
        }`,
        value: formatNumber({
          input: Number(fee.amount.amount) || 0,
          currency: fee.amount.currency,
        }),
        tooltip: `${toSentenceCase(fee.type)} fee includes ${getPercentage(
          quote.vatRate,
        )} VAT for companies registered in ${quote.country.name}`,
      })),
      ...(showInvoiceQuoteDetails
        ? []
        : [
            {
              item: InvoiceItems.TOTAL,
              label: TOTAL_LABEL,
              value: formatNumber({
                input: Number(quote.transferAmount.amount),
                currency: quote.transferAmount.currency,
              }),
            },
          ]),
    ];
    setQuoteDescriptionItems(listItems);
  }, [invoice?.raisedAt, quote, showInvoiceQuoteDetails]);

  const descriptionListItems = quoteDescriptionItems.map(
    (item: QuoteDescriptionItems, index) => ({
      header: (
        <Inline gap="4" align="center" minH={24}>
          <Typography as="label" size="14" color="default" weight="medium">
            {item.label}
          </Typography>
          {item.tooltip && (
            <Tooltip id={`tooltip-${index}`} description={item.tooltip}>
              <Icon size="16" icon="Help" title="" />
            </Tooltip>
          )}
        </Inline>
      ),
      detail: (
        <Inline justify="flex-end">
          <Typography
            size="14"
            as="span"
            weight={item.item === InvoiceItems.TOTAL ? 'medium' : 'regular'}
            italic={item.item === InvoiceItems.EXCHANGE_RATE}
            color={
              item.item === InvoiceItems.EXCHANGE_RATE ? 'subtle' : 'default'
            }
          >
            {item.value}
          </Typography>
        </Inline>
      ),
    }),
  ) as DescriptionListItem[];

  return { descriptionListItems, isLoading: isInvoiceLoading };
};
